import React from 'react';

import { css } from 'config/emotionCss';
import { Col, Input, Row } from 'ui/base';
import FormField from 'ui/form/FormField';

import { DEFAULT_NB_COLUMN, rowColFormatter, validatorHandler } from './utils';

const simplePanelStyle = css`
  .ant-form-item {
    margin-bottom: 0;
  }
`;

const generateName = (basename, name) => (basename ? `${basename}.${name}` : name);

const NB_MAX_COLUMNS = 24;

/**
 * @template T
 * @typedef {{
 *   name?: string,
 *   help?: React.ReactNode,
 *   component?: JSX.Element,
 *   layout?: import("antd").ColProps,
 *   componentProps?: unknown,
 *   formFieldProps?: import("ui/form/FormField").FormFieldProps,
 *   newLine?: boolean,
 * } & Omit<import("ui/form/FormField").FormFieldProps<T>, 'children'> } FieldConfig
 */

/** @typedef {FieldConfig<T>[]} FieldsConfig */
/** @typedef {{ baseName?: string, config: FieldsConfig, nbColumn?: number, formFieldProps?: Partial<import("ui/form/FormField").FormFieldProps<any>> }} SimpleFieldsPanelProps */

/** @type {React.FC<SimpleFieldsPanelProps>} */
const SimpleFieldsPanel = ({ baseName, config, formFieldProps, nbColumn = DEFAULT_NB_COLUMN }) => {
  const rows = React.useMemo(() => rowColFormatter(config, nbColumn), [config, nbColumn]);

  const xlSize = Math.ceil(NB_MAX_COLUMNS / nbColumn);

  return rows.map(row => {
    const key = row.rowKeys.join('-');

    return (
      <Row key={key} type="flex" gutter={[24, { xs: 8, sm: 8, xl: 16 }]} className={simplePanelStyle}>
        {row.children.map(({ key: fieldKey, name, component: Component = Input, layout, validate, componentProps, ...rest }, index) => {
          const colSize = rest.newLine && rest.type === 'checkbox' && index === 0 ? NB_MAX_COLUMNS : xlSize;

          return (
            <Col key={fieldKey ?? name} xs={24} xl={colSize} {...layout}>
              <FormField
                validateOnInit
                name={generateName(baseName, name)}
                validate={validatorHandler(validate)}
                subscription={{ value: true, error: true, touched: true }}
                {...formFieldProps}
                {...rest}
              >
                {input => (
                  React.isValidElement(Component) ? (
                    React.cloneElement(Component, { ...input, 'data-cy': name, ...componentProps })
                  ) : (
                    <Component {...input} data-cy={name} {...componentProps} />
                  )
                )}
              </FormField>
            </Col>
          );
        })}
      </Row>
    );
  });
};

export default SimpleFieldsPanel;
